import type { InstrumentPanel as InstrumentPanelT } from 'lib-analytics-types';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { forwardRef, HTMLAttributes, useState } from 'react';

import { InstrumentPanelDragHandle } from './components/dragHandle/instrumentPanelDragHandle';
import { InstrumentPanelTitle } from './components/title/instrumentPanelTitle';
import styles from './instrumentPanel.module.scss';

export type InstrumentPanelProps = {
  /** The instrument panel. */
  panel: InstrumentPanelT;
  /** A list of elements (buttons etc.) to perform actions on the panel. */
  actionElements?: JSX.Element | JSX.Element[];
  /** Whether to show the panel header. */
  showHeader?: boolean;
  /** Whether a custom draggable handle should be shown in the panel header. */
  isDraggable?: boolean;
} & PropsWithChildren &
  Pick<HTMLAttributes<HTMLDivElement>, 'style'>;

export const InstrumentPanel = forwardRef<HTMLDivElement, InstrumentPanelProps>(
  function InstrumentPanel(
    {
      children,
      panel,
      actionElements,
      showHeader = true,
      isDraggable = false,
      ...divProps
    },
    ref,
  ): JSX.Element {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        ref={ref}
        className={styles.panel}
        data-analytics-panel={panel.id}
        {...divProps}
        {...(isDraggable
          ? {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            }
          : {})}
      >
        {showHeader && (
          <div className={styles.header}>
            {isHovered && <InstrumentPanelDragHandle />}

            <InstrumentPanelTitle panel={panel} />

            {actionElements && (
              <div className={styles.actions}>{actionElements}</div>
            )}
          </div>
        )}

        <div className={styles.content}>{children}</div>
      </div>
    );
  },
);
